import React, { useState } from "react";
import { useDocuments } from "../../hooks/useDocuments";
import { Loader } from "../../../../components/common/loader";
import { Text } from "../../../../components/common/typography";
import { Badge } from "../../../../components/common/badge";
import type { DocumentDto, DocumentDtoStatusEnum, DocumentDtoTypeEnum, } from "../../../../api/api";
import { documentsService } from "../../../api_client";
import { DocumentPreviewModal } from "../../../admin-documents/presentation/components/document-preview-modal";
import { showErrorToast, showSuccessToast } from "../../../../utils/toasts";
import { IconDownload, IconEye, IconFileDescription } from "@tabler/icons-react";
const getStatusBadge = (status: DocumentDtoStatusEnum) => {
    switch (status) {
        case "PENDING":
            return <Badge label="Pending" state="pending"/>;
        case "APPROVED":
            return <Badge label="Approved" state="success"/>;
        case "REJECTED":
            return <Badge label="Rejected" state="error"/>;
        default:
            return <Badge label={status} state="pending"/>;
    }
};
const getDocumentTypeDisplayName = (type: DocumentDtoTypeEnum): string => {
    switch (type) {
        case "CR":
            return "Commercial Registration";
        case "TAX_CARD":
            return "Tax Card";
        default:
            return type;
    }
};
const handleDownload = async (documentId: string, fileName: string) => {
    try {
        const response = await documentsService.getDocumentDownloadUrl(documentId);
        const downloadUrl = response.data.url;
        if (downloadUrl) {
            // Create a temporary link and trigger download
            const link = document.createElement("a");
            link.href = downloadUrl;
            link.download = fileName;
            link.target = "_blank";
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            showSuccessToast("Download started");
        }
        else {
            showErrorToast("Download URL not available");
        }
    }
    catch (error) {
        console.error("Download error:", error);
        showErrorToast("Failed to download document");
    }
};
interface DocumentsSectionProps {
    documents?: DocumentDto[];
    showOnlyRequired?: boolean;
    requiredTypes?: (DocumentDtoTypeEnum | string)[];
}
export const DocumentsSection: React.FC<DocumentsSectionProps> = ({ documents: providedDocuments, showOnlyRequired = false, requiredTypes = [], }) => {
    const [previewDocument, setPreviewDocument] = useState<DocumentDto | null>(null);
    const [isPreviewOpen, setIsPreviewOpen] = useState(false);
    const { data, isLoading, error } = useDocuments({
        page: 1,
        pageSize: 100,
    });
    // Use provided documents or fetch from API
    const documents = providedDocuments || data?.data || [];
    // Filter documents if showOnlyRequired is true
    const filteredDocuments = showOnlyRequired && requiredTypes.length > 0
        ? documents.filter((doc) => requiredTypes.includes(doc.type))
        : documents;
    if (!providedDocuments && isLoading) {
        return (<div className="flex justify-center py-8">
        <Loader inline size="sm" text="Loading documents..."/>
      </div>);
    }
    if (!providedDocuments && error) {
        return (<div className="text-center py-8">
        <Text variant="body" className="text-accentError">
          Failed to load documents. Please try again later.
        </Text>
      </div>);
    }
    if (filteredDocuments.length === 0) {
        return (<div className="text-center py-8">
        <IconFileDescription size={40} style={{
                color: "#6B7280",
                marginBottom: 2
            }}/>
        <Text variant="body" tone="body">
          No documents uploaded yet
        </Text>
      </div>);
    }
    const handlePreview = (document: DocumentDto) => {
        setPreviewDocument(document);
        setIsPreviewOpen(true);
    };
    return (<>
      <div className="flex flex-col gap-4">
        {filteredDocuments.map((document) => (<div key={document.id} className="flex items-center justify-between p-4 border border-uiSecondary rounded-lg hover:bg-uiSecondary transition-colors">
            <div className="flex items-center gap-4 flex-1">
              <IconFileDescription size={24} style={{
                color: "#6B7280"
            }}/>
              <div className="flex flex-col flex-1">
                <Text variant="body" tone="dark">
                  {getDocumentTypeDisplayName(document.type)}
                </Text>
                <Text variant="bodyXs" tone="body" className="mt-1">
                  {document.fileName}
                </Text>
                {document.status === "REJECTED" && document.rejectionReason && (<Text variant="bodyXs" className="mt-1 text-accentError">
                    Reason: {document.rejectionReason}
                  </Text>)}
              </div>
            </div>
            <div className="flex items-center gap-3">
              {getStatusBadge(document.status)}
              <button onClick={() => handlePreview(document)} className="p-2 hover:bg-uiSecondary rounded-lg transition-colors" aria-label="Preview document">
                <IconEye size={20} style={{
                color: "#6B7280"
            }}/>
              </button>
              <button onClick={() => handleDownload(document.id, document.fileName)} className="p-2 hover:bg-uiSecondary rounded-lg transition-colors" aria-label="Download document">
                <IconDownload size={20} style={{
                color: "#6B7280"
            }}/>
              </button>
            </div>
          </div>))}
      </div>

      {previewDocument && (<DocumentPreviewModal document={previewDocument} open={isPreviewOpen} onClose={() => {
                setIsPreviewOpen(false);
                setPreviewDocument(null);
            }} canTakeAction={false}/>)}
    </>);
};
export default DocumentsSection;
